import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Empty, ErrorMessage, Loading, Notice, PageHeader, StatusBadge } from '../components'
import { createPrivacyPolicy, createTermsOfService, getPrivacyPolicies, getTermsOfService } from '../lib/api'
import { sanitizeLegalHtml } from '../lib/legalHtml'
import type { LegalDocument } from '../types'

type DocumentKind = 'terms' | 'privacy'
type EditorState = { kind: DocumentKind; version: number; initialText: string }

const labels: Record<DocumentKind, { title: string; description: string }> = {
  terms: { title: 'Termos de uso', description: 'Condições de uso da plataforma.' },
  privacy: { title: 'Política de privacidade', description: 'Como os dados pessoais são tratados.' },
}

function ordered(items: LegalDocument[] | undefined) {
  return [...(items || [])].sort((a, b) => b.version - a.version)
}

export function LegalDocumentsPage() {
  const client = useQueryClient()
  const editorRef = useRef<HTMLDivElement>(null)
  const [kind, setKind] = useState<DocumentKind>('terms')
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [draftText, setDraftText] = useState('')
  const [notice, setNotice] = useState('')
  const terms = useQuery({ queryKey: ['legal-documents', 'terms'], queryFn: getTermsOfService })
  const privacy = useQuery({ queryKey: ['legal-documents', 'privacy'], queryFn: getPrivacyPolicies })
  const documents = kind === 'terms' ? ordered(terms.data?.results) : ordered(privacy.data?.results)
  const current = documents[0]
  const save = useMutation({
    mutationFn: (value: EditorState) => {
      const data = { version: value.version, text: sanitizeLegalHtml(draftText) }
      if (value.kind === 'terms') return createTermsOfService(data)
      return createPrivacyPolicy(data)
    },
    onSuccess: async (_value, value) => {
      await client.invalidateQueries({ queryKey: ['legal-documents', value.kind] })
      setEditor(null)
      setNotice(`${labels[value.kind].title} v${value.version} salva com sucesso.`)
    },
  })

  const initialEditorText = editor?.initialText
  useEffect(() => {
    if (editorRef.current && initialEditorText !== undefined) editorRef.current.innerHTML = initialEditorText
  }, [initialEditorText])

  function openEditor(item: LegalDocument | undefined) {
    const base = item || current
    setNotice('')
    setDraftText(base?.text || '')
    setEditor({ kind, version: (current?.version || 0) + 1, initialText: base?.text || '' })
  }

  function execute(command: string, value?: string) {
    editorRef.current?.focus()
    document.execCommand(command, false, value)
    setDraftText(editorRef.current?.innerHTML || '')
  }

  function removeCopiedStyles() {
    const content = editorRef.current
    if (!content) return
    const clean = sanitizeLegalHtml(content.innerHTML)
    content.innerHTML = clean
    setDraftText(clean)
  }

  function addLink() {
    const href = window.prompt('Endereço do link')
    if (href?.trim()) execute('createLink', href.trim())
  }

  const loading = terms.isLoading || privacy.isLoading
  const error = terms.error || privacy.error
  return <section>
    <PageHeader eyebrow="Configuração" title="Documentos legais" description="Publique novas versões e preserve o histórico dos documentos já aceitos." />
    {notice && <Notice>{notice}</Notice>}
    {error && <ErrorMessage error={error} />}
    {loading ? <Loading label="Carregando documentos…" /> : <>
      <div className="legal-document-tabs" role="tablist" aria-label="Documentos legais">
        {(Object.keys(labels) as DocumentKind[]).map(item => <button key={item} className={`button ${kind === item ? 'button-primary' : 'button-secondary'}`} type="button" role="tab" aria-selected={kind === item} onClick={() => { setKind(item); setEditor(null); setNotice('') }}>{labels[item].title}</button>)}
      </div>
      <div className="panel section-panel legal-document-panel">
        <div className="panel-heading"><div><h2>{labels[kind].title}</h2><p>{labels[kind].description} A versão mais alta é a atual.</p></div><div className="button-row"><button className="button button-primary" type="button" onClick={() => openEditor(current)}>Nova versão</button></div></div>
        {!documents.length ? <Empty title="Nenhuma versão cadastrada" detail="Crie a primeira versão para disponibilizar este documento." /> : <div className="table-wrap"><table><thead><tr><th>Versão</th><th>Conteúdo</th><th>Status</th><th /></tr></thead><tbody>{documents.map((item, index) => <tr key={item.id}><td><strong>v{item.version}</strong></td><td><span className="legal-document-snippet">{item.text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() || 'Sem conteúdo'}</span></td><td><StatusBadge tone={index === 0 ? 'success' : 'neutral'}>{index === 0 ? 'Atual' : 'Histórica'}</StatusBadge></td><td><button className="button button-ghost" type="button" onClick={() => openEditor(item)}>Usar como base de nova versão</button></td></tr>)}</tbody></table></div>}
      </div>
    </>}
    {editor && <div className="modal-backdrop" role="presentation"><section className="modal legal-editor-modal" role="dialog" aria-modal="true" aria-labelledby="legal-editor-title"><header><h2 id="legal-editor-title">{`Nova versão de ${labels[kind].title}`}</h2><button className="icon-button" type="button" onClick={() => !save.isPending && setEditor(null)} aria-label="Fechar">×</button></header><form onSubmit={event => { event.preventDefault(); save.mutate(editor) }}><label className="field"><span>Versão</span><input type="number" min={(current?.version || 0) + 1} value={editor.version} onChange={event => setEditor(value => value && { ...value, version: Number(event.target.value) })} disabled={save.isPending} /><small>Uma nova versão fica disponível para o fluxo de leitura e aceite.</small></label><div className="rich-editor"><div className="rich-editor-toolbar" role="toolbar" aria-label="Formatação"><button type="button" onClick={() => execute('formatBlock', 'p')}>Parágrafo</button><button type="button" onClick={() => execute('formatBlock', 'h2')}>Título</button><button type="button" onClick={() => execute('bold')}><strong>Negrito</strong></button><button type="button" onClick={() => execute('italic')}><em>Itálico</em></button><button type="button" onClick={addLink}>Link</button><button type="button" onClick={() => execute('insertUnorderedList')}>Lista</button><button type="button" onClick={() => execute('insertOrderedList')}>Lista numerada</button><button type="button" onClick={removeCopiedStyles} title="Remove estilos trazidos de outro editor">Remover estilos</button></div><div ref={editorRef} className="rich-editor-content" contentEditable suppressContentEditableWarning role="textbox" aria-label="Conteúdo do documento" onInput={event => setDraftText(event.currentTarget.innerHTML)} /></div>{save.error && <ErrorMessage error={save.error} />}<div className="modal-actions"><button className="button button-secondary" type="button" onClick={() => setEditor(null)} disabled={save.isPending}>Cancelar</button><button className="button button-primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Publicar nova versão'}</button></div></form></section></div>}
  </section>
}
